package com.example.mystybag.appCore.model.addMerchantLocation

import com.example.mystybag.core.model.CityItem
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.sharedmodels.project.responseModels.nomenclatures.AllergenModel

data class MerchantRegistrationAddMerchantLocationDetailsUIState(
    val isEditMode: Boolean = false,
    val selectedTab: MerchantLocationTab = MerchantLocationTab.BUSINESS,
    val expandedTab: MerchantLocationTab? = MerchantLocationTab.BUSINESS,
    val storeName: String = "",
    val storeAddress: String = "",
    val cityId: Long? = null,
    val cityName: String = "",
    val storePhoneNumber: String = "",
    val dialCode: String = "",
    val packaging: String = "",
    val additionalPickupInstructions: String = "",
    val surpriseBagContainsBuffetFood: Boolean = false,
    val cities: List<CityItem> = emptyList(),
    val countries: List<CountryItem> = emptyList(),
    val isLoading: Boolean = false,
    val showCityPicker: Boolean = false,
    val showCountryPicker: Boolean = false,
    val latitude: Double? = null,
    val longitude: Double? = null,
    /** True when map coordinates are accepted: geocoded from address/city, user moved the pin, or edit pre-fill. */
    val mapLocationConfirmedByUser: Boolean = false,
    val isMapReady: Boolean = false,
    /** Allergen nomenclature options for the per-product allergen picker. */
    val availableAllergens: List<AllergenModel> = emptyList(),
    val selectedBusinessCategory: BusinessCategoryItem? = null,
    val businessCategories: List<BusinessCategoryItem> = emptyList(),
    val showBusinessCategoryPicker: Boolean = false,
    val selectedPreviewImageIndex: Int? = null,
    val productRows: List<ProductRowUIModel> = listOf(ProductRowUIModel()),
    // --- Embedded product catalog (local in-memory draft tree) ---
    val catalogCategories: List<CatalogCategoryDraft> = emptyList(),
    val catalogProducts: List<CatalogProductDraft> = emptyList(),
    val catalogExpandedLocalIds: Set<Long> = emptySet(),
    /** Server ids of categories/products removed locally; deleted on submit. */
    val catalogDeletedCategoryServerIds: List<Long> = emptyList(),
    val catalogDeletedProductServerIds: List<Long> = emptyList(),
    /** The inline add/edit editor currently shown in the tree, or null. */
    val catalogEditor: CatalogEditorState? = null,
    val existingMenuPhotoUrls: List<String> = emptyList(),
    val menuPhotoFileNames: List<String> = emptyList(),
    val errorMessage: String? = null,
    val requiredFieldErrors: Set<LocationDetailsRequiredField> = emptySet(),
    val requiredProductNameRows: Set<Int> = emptySet(),
    val requiredProductGramsRows: Set<Int> = emptySet(),
    val focusTargetField: LocationDetailsRequiredField? = null,
    val validationTargetTab: MerchantLocationTab? = null,
    val validationRequestId: Int = 0
)

data class BusinessCategoryItem(
    val id: Long,
    val title: String,
    val description: String,
    val coverImageUrls: List<String> = emptyList()
)

/**
 * A category (folder) in the locally-edited catalog tree. [localId] is a
 * client-only stable handle; [serverId] is set once the item exists on the
 * server (from edit prefill or after a successful sync).
 */
data class CatalogCategoryDraft(
    val localId: Long,
    val serverId: Long? = null,
    /** null = top-level (root) category. */
    val parentLocalId: Long? = null,
    val name: String,
    val sortOrder: Int = 0
)

/** A product in the locally-edited catalog tree; always inside a category. */
data class CatalogProductDraft(
    val localId: Long,
    val serverId: Long? = null,
    val categoryLocalId: Long,
    val name: String,
    val grams: Int = 0,
    val description: String = "",
    val isEnabled: Boolean = true,
    val sortOrder: Int = 0,
    /** Allergen ids (nomenclature_allergens) attached to this product. */
    val allergenIds: List<Long> = emptyList()
)

enum class CatalogEditorKind { CATEGORY, PRODUCT }

/** Inline add/edit editor state rendered in the catalog tree. */
data class CatalogEditorState(
    val kind: CatalogEditorKind,
    /**
     * For CATEGORY: the parent category (null = new root category).
     * For PRODUCT: the owning category (never null).
     */
    val parentLocalId: Long? = null,
    /** null = creating a new item; otherwise the localId being edited. */
    val editingLocalId: Long? = null,
    val nameInput: String = "",
    val gramsInput: String = "",
    val nameError: Boolean = false,
    val gramsError: Boolean = false,
    /** Selected allergen ids while editing a PRODUCT. */
    val selectedAllergenIds: Set<Long> = emptySet()
)

enum class LocationDetailsRequiredField {
    STORE_NAME,
    CITY,
    DIAL_CODE,
    PHONE,
    MAP_LOCATION,
    BUSINESS_CATEGORY,
    COVER_PREVIEW
}

enum class MerchantLocationTab {
    DETAILS,
    LOCATION,
    BUSINESS,
    PRODUCTS,
    MENU
}

